import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import type { TooltipProps } from 'antd';

import { ColorsType } from '@common/tokens';
import { Icon, IconOfKind } from '@common/components/icon';

const LEFT_OFFSET_PX = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
} as const;

export type leftOffsetType = keyof typeof LEFT_OFFSET_PX;

export type InfoTooltipKindType = 'default' | 'warn' | 'solidInfo';

const ICON_BY_KIND: Record<InfoTooltipKindType, IconOfKind> = {
  default: { kind: 'light', icon: 'circleInfo' },
  warn: { kind: 'solid', icon: 'triangleExclamation' },
  solidInfo: { kind: 'solid', icon: 'circleInfo' },
};

type InfoTooltipProps = {
  title: TooltipProps['title'];
  kind?: InfoTooltipKindType;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  leftOffset?: leftOffsetType;
  iconColor?: ColorsType;
  placement?: TooltipProps['placement'];
  overlayMaxWidth?: number;
};

function ariaLabelForTitle(title: TooltipProps['title']): string {
  return typeof title === 'string' ? title : 'More information';
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  kind = 'default',
  size = 'lg',
  leftOffset = 'sm',
  iconColor = 'midnightLightest',
  placement,
  overlayMaxWidth,
}) => {
  const marginLeft = LEFT_OFFSET_PX[leftOffset];
  const iconOfKind = ICON_BY_KIND[kind];
  const overlayStyle =
    overlayMaxWidth !== undefined ? { maxWidth: overlayMaxWidth } : undefined;
  const ariaLabel = useMemo(() => ariaLabelForTitle(title), [title]);

  return (
    <Tooltip title={title} placement={placement} overlayStyle={overlayStyle}>
      <span style={{ marginLeft }} tabIndex={0} aria-label={ariaLabel}>
        <Icon
          className="wk-info-tooltip"
          color={iconColor}
          size={size}
          {...iconOfKind}
        />
      </span>
    </Tooltip>
  );
};
